/**
 * 地区 GIS データ管理ページ (docs/22 要件1/2/3、Sprint G2)。
 * `/districts/:districtId/gis`。master + 担当 district admin が到達可能 (rules で認可)。
 *
 *   - GIS ON/OFF トグル (要件1)
 *   - ファイルインポート (.gpkg/.kml/.kmz/.geojson → importGisLayer、要件2)
 *   - レイヤー一覧 + スタイル編集 + 表示順 + デフォルト表示 + 削除 (要件3)
 *   - ライブプレビュー地図 (要件3)
 */

import { Button, Card, Checkbox, Spinner, useToast } from '@citizen-walk/shared-ui';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { GisLayer, GisLayerStyle } from '@citizen-walk/shared-types';
import { useAdminAuth } from '../auth';
import { GisLayerStyleEditor } from './GisLayerStyleEditor';
import { GisPreviewMap } from './GisPreviewMap';
import {
  deleteGisLayer,
  importGisFile,
  moveGisLayer,
  setGisEnabled,
  updateGisLayer,
  useGisConfig,
  useGisLayers,
} from './useGisAdmin';

const ACCEPT = '.gpkg,.kml,.kmz,.geojson,.json';

export function GisAdminPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const uid = authState.user?.uid ?? '';
  const { toast } = useToast();

  const config = useGisConfig(districtId ?? null);
  const layersState = useGisLayers(districtId ?? null);

  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  if (!districtId) return null;

  async function handleToggle(enabled: boolean) {
    setBusy(true);
    try {
      await setGisEnabled(districtId!, enabled, uid);
      toast('success', enabled ? 'GIS 機能を有効にしました' : 'GIS 機能を無効にしました');
    } catch (e) {
      toast('error', `更新失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleImport() {
    if (!selectedFile) return;
    setBusy(true);
    try {
      const res = await importGisFile(districtId!, selectedFile);
      const warned = res.layers.filter((l) => l.warning);
      toast(
        warned.length ? 'info' : 'success',
        `${res.layers.length} レイヤーを取り込みました` +
          (warned.length ? ` (${warned.length} 件は大規模データの警告あり)` : ''),
      );
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast('error', `インポート失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveStyle(layerId: string, style: GisLayerStyle) {
    setBusy(true);
    try {
      await updateGisLayer(districtId!, layerId, { style }, uid);
      toast('success', 'スタイルを保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const layers = layersState.status === 'ready' ? layersState.layers : [];

  return (
    <>
      <header className="mb-6">
        <Link
          to={`/districts/${districtId}`}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="mt-2 text-2xl font-bold text-slate-900">GIS データ管理</h1>
        <p className="mt-1 text-sm text-slate-500">
          GeoPackage / KML / GeoJSON を取り込み、ユーザーアプリの地図に重ねて表示します。
        </p>
      </header>

      {/* 要件1: ON/OFF */}
      <Card padding="lg" className="mb-4">
        <h2 className="mb-2 text-base font-bold text-slate-900">GIS 機能</h2>
        {config.status === 'loading' ? (
          <Spinner />
        ) : (
          <Checkbox
            label="この地区で GIS 機能を有効にする (ユーザーアプリの地図・メニューに反映)"
            checked={config.enabled}
            onChange={(c) => void handleToggle(c)}
            disabled={busy}
          />
        )}
      </Card>

      {/* 要件2: インポート */}
      <Card padding="lg" className="mb-4">
        <h2 className="mb-2 text-base font-bold text-slate-900">データをインポート</h2>
        <p className="mb-3 text-xs text-slate-500">
          対応形式: .gpkg (GeoPackage) / .kml / .kmz / .geojson。1
          ファイルに複数レイヤーを含む場合は まとめて取り込みます。
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            disabled={busy}
            className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-sm file:text-primary-700 hover:file:bg-primary-100"
          />
          <Button size="sm" onClick={() => void handleImport()} disabled={!selectedFile || busy}>
            {busy ? '処理中…' : 'インポート'}
          </Button>
        </div>
      </Card>

      {/* 要件3: レイヤー一覧 + スタイル */}
      <Card padding="lg">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">レイヤー ({layers.length})</h2>
          {layers.length > 0 && (
            <Button size="sm" variant="secondary" onClick={() => setShowPreview((v) => !v)}>
              {showPreview ? 'プレビューを隠す' : 'プレビューを表示'}
            </Button>
          )}
        </div>

        {showPreview && layers.length > 0 && (
          <GisPreviewMap
            layers={layers}
            className="mb-4 h-72 w-full overflow-hidden rounded-md border border-slate-200"
          />
        )}

        {layersState.status === 'loading' && <Spinner />}
        {layersState.status === 'ready' && layers.length === 0 && (
          <p className="text-sm text-slate-500">
            まだレイヤーがありません。上のフォームから取り込んでください。
          </p>
        )}
        <ul className="grid grid-cols-1 gap-3">
          {layers.map((layer, i) => (
            <LayerRow
              key={layer.id}
              districtId={districtId}
              uid={uid}
              layer={layer}
              busy={busy}
              isFirst={i === 0}
              isLast={i === layers.length - 1}
              allLayers={layers}
              onSaveStyle={(style) => void handleSaveStyle(layer.id, style)}
              setBusy={setBusy}
              toast={toast}
            />
          ))}
        </ul>
      </Card>
    </>
  );
}

interface LayerRowProps {
  districtId: string;
  uid: string;
  layer: GisLayer;
  allLayers: GisLayer[];
  busy: boolean;
  isFirst: boolean;
  isLast: boolean;
  onSaveStyle: (style: GisLayerStyle) => void;
  setBusy: (b: boolean) => void;
  toast: (type: 'success' | 'error' | 'info', msg: string) => void;
}

function LayerRow({
  districtId,
  uid,
  layer,
  allLayers,
  busy,
  isFirst,
  isLast,
  onSaveStyle,
  setBusy,
  toast,
}: LayerRowProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(layer.name);

  async function saveName() {
    if (name.trim() === layer.name || !name.trim()) {
      setName(layer.name);
      return;
    }
    setBusy(true);
    try {
      await updateGisLayer(districtId, layer.id, { name: name.trim() }, uid);
    } catch (e) {
      toast('error', `名前の保存失敗: ${(e as Error).message}`);
      setName(layer.name);
    } finally {
      setBusy(false);
    }
  }

  async function toggleVisible(visible: boolean) {
    setBusy(true);
    try {
      await updateGisLayer(districtId, layer.id, { defaultVisible: visible }, uid);
    } catch (e) {
      toast('error', `更新失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function move(dir: -1 | 1) {
    setBusy(true);
    try {
      await moveGisLayer(districtId, allLayers, layer.id, dir, uid);
    } catch (e) {
      toast('error', `並び替え失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`レイヤー「${layer.name}」を削除しますか？ (元に戻せません)`)) return;
    setBusy(true);
    try {
      await deleteGisLayer(districtId, layer.id);
      toast('success', 'レイヤーを削除しました');
    } catch (e) {
      toast('error', `削除失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => void saveName()}
          disabled={busy}
          className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-sm font-semibold text-slate-800"
        />
        <span className="text-xs text-slate-500">
          {geometryLabel(layer.geometryType)} ・ {layer.featureCount} 件 ・{' '}
          {Math.round(layer.sizeBytes / 1024)}KB ・ {layer.sourceFormat}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Checkbox
          label="既定で表示"
          checked={layer.defaultVisible}
          onChange={(c) => void toggleVisible(c)}
          disabled={busy}
        />
        <span className="flex items-center gap-1">
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || isFirst}
            onClick={() => void move(-1)}
          >
            ▲
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || isLast}
            onClick={() => void move(1)}
          >
            ▼
          </Button>
        </span>
        <Button size="sm" variant="secondary" onClick={() => setEditing((v) => !v)}>
          {editing ? 'スタイルを閉じる' : 'スタイル編集'}
        </Button>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => void remove()}>
          削除
        </Button>
      </div>
      {editing && (
        <div className="mt-3">
          <GisLayerStyleEditor layer={layer} busy={busy} onSave={onSaveStyle} />
        </div>
      )}
    </li>
  );
}

function geometryLabel(gt: GisLayer['geometryType']): string {
  switch (gt) {
    case 'point':
      return '点';
    case 'line':
      return '線';
    case 'polygon':
      return '面';
    default:
      return '混在';
  }
}
